import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Icon, Image, Text, Link } from "@chakra-ui/react";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import * as SocialProviders from "../../../_constants/SocialProvider";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import { getUrl } from "../../../_utils/url";
// import { formatPostMessage } from "../../../_utils/postMessageFormatter";
import { getFixedPhotosVideosOrder } from "../PostEditor/helpers";
import { formatDuration } from "../../../_utils/common";

const SocialPostCardPinterest = ({ account, post, maxWidth, ...rest }) => {
  const type = account?.social_type;
  const [hover, setHover] = useState(false);

  const additionalFields = useMemo(() => {
    const additional = post?.additional || [];

    return additional.find(
      (item) =>
        item.source === SocialProviders.PINTEREST &&
        item.type === PostAdditionalTypes.POST
    );
  }, [post]);

  const handleMouseEnter = useCallback(() => setHover(true), [setHover]);

  const handleMouseLeave = useCallback(() => setHover(false), [setHover]);

  if (!account || !post || !type) {
    return null;
  }

  const renderFooter = () => (
    <Box>
      <Box sx={{ padding: "8px 6px" }}>
        {additionalFields?.title ? (
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: "700",
              lineHeight: "1.4",
              marginBottom: "4px",
              color: "#111",
            }}
          >
            <Text noOfLines="2" title={additionalFields.title}>
              {additionalFields.title}
            </Text>
          </Box>
        ) : null}
        {/* {post?.message ? (
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "1.25",
              "& a": {
                color: "#216fdb",
              },
              marginTop: "4px",
              color: "#111",
            }}
          >
            <Text
              noOfLines="4"
              title={post.message}
              dangerouslySetInnerHTML={{
                __html: formatPostMessage(post.message),
              }}
            />
          </Box>
        ) : null} */}
      </Box>
    </Box>
  );

  const renderMedia = () => {
    const maxItems = 1;
    const mediaItems = getFixedPhotosVideosOrder(
      post?.photos,
      post?.videos
    ).items;
    const mediaItemsSliced = mediaItems.slice(0, maxItems);
    const url = getUrl(additionalFields?.link || "");

    const mediaItemStyles = {
      display: "inline-block",
      width: "100%",
      height: "auto",
      verticalAlign: "middle",
    };

    return (
      <Box>
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseOver={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "relative",
            maxHeight: "496px",
            overflow: "hidden",
            borderRadius: "16px",
          }}
        >
          {mediaItemsSliced.map((item) => (
            <Box key={item.data.url}>
              <Box>
                {item.field === "videos" ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: "full",
                      height: "full",
                    }}
                  >
                    <Box
                      as="video"
                      sx={mediaItemStyles}
                      src={item.data.url}
                      controls={hover}
                      preload="metadata"
                      poster={item.data.thumbUrl}
                      alt={additionalFields?.imageAlt}
                      title={additionalFields?.imageAlt}
                    />
                    {hover ? null : (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "0",
                          left: "0",
                          margin: "8px",
                          padding: "7px 6px",
                          background: "rgba(255, 255, 255, 0.8)",
                          borderRadius: "18px",
                          color: "#111111",
                          fontSize: "10px",
                          lineHeight: "1em",
                        }}
                      >
                        {formatDuration(item.data?.meta?.duration)}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Image
                    sx={mediaItemStyles}
                    src={item.data.url}
                    alt={additionalFields?.imageAlt}
                    title={additionalFields?.imageAlt}
                  />
                )}
              </Box>
            </Box>
          ))}
          {additionalFields?.link && !hover ? (
            <Box
              sx={{
                position: "absolute",
                left: "0",
                right: "0",
                bottom: "0",
                padding: "12px",
              }}
            >
              <Link
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "rgb(51, 51, 51)",
                  background: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "16px",
                  padding: "0 12px",
                  height: "32px",
                  maxWidth: "132px",
                  _hover: {
                    backgroundColor: "#ffffff",
                    textDecoration: "none",
                    color: "#111",
                  },
                }}
                href={additionalFields.link}
                title={additionalFields.link}
                isExternal
              >
                <Icon
                  viewBox="0 0 24 24"
                  width="9px"
                  height="9px"
                  sx={{ marginRight: "8px" }}
                >
                  {/* eslint-disable-next-line max-len */}
                  <path d="M4.928 1a2.357 2.357 0 1 0 0 4.714h10.024L1.69 18.976a2.36 2.36 0 0 0 0 3.334 2.35 2.35 0 0 0 1.668.69c.603 0 1.206-.229 1.667-.69l13.26-13.263v10.024a2.358 2.358 0 1 0 4.715 0V1H4.928Z" />
                </Icon>
                <Text as="span" noOfLines="1">
                  {url?.hostname || ""}
                </Text>
              </Link>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  };

  const showMedia = !!post?.videos?.length || !!post?.photos?.length;

  return (
    <Box
      sx={{
        background: "white",
        // eslint-disable-next-line max-len
        fontFamily:
          "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen-Sans,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,ヒラギノ角ゴ Pro W3,Hiragino Kaku Gothic Pro,メイリオ,Meiryo,ＭＳ Ｐゴシック,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol",
        maxWidth: maxWidth,
      }}
      {...rest}
    >
      <Box sx={{ padding: "8px" }}>
        {showMedia ? renderMedia() : null}
        {renderFooter()}
      </Box>
    </Box>
  );
};

SocialPostCardPinterest.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  maxWidth: PropTypes.string,
};

SocialPostCardPinterest.defaultProps = {
  maxWidth: "252px",
};

export default SocialPostCardPinterest;
